import * as mutationTypes from './mutation-types'
import { v4 as generateUuid } from 'uuid'
import { SfLog } from '@app/adapters/logger'
import { symmetricDifference } from '@app/utils/set-helpers'
import fuzzysort from 'fuzzysort'

import api from '../api'

const MAX_RETRIES = 5

const getters = {
  companiesProcessedPercentage(state, getters) {
    const { allCompaniesCount } = getters

    if (allCompaniesCount === 0) {
      return state.allCompanyIds === null ? 0 : 100
    }

    // in case the correct counts are not yet reset after switching segments/filters,
    // return 0 to avoid showing >100% percentages in this situation
    if (allCompaniesCount < state.collectedCompanyIds.size) {
      return 0
    }
    return Math.floor(
      (state.collectedCompanyIds.size / allCompaniesCount) * 100
    )
  },
  selectedLedgers(state) {
    return Object.keys(state.companies).reduce((ledgerIds, key) => {
      if (state.selectedCompanies.has(Number(key))) {
        ledgerIds.push(state.companies[key].filter_values.selectedPeriod.id)
      }
      return ledgerIds
    }, [])
  },
  allCompaniesCount(state) {
    return state.allCompanyIds === null ? 0 : state.allCompanyIds.size
  },
  allCompaniesProcessed(_, getters) {
    return getters.companiesProcessedPercentage === 100
  },
  getAllCompanies(state) {
    return Object.values(state.companies)
  },
  sortedAndFilteredCompanies(state, getters) {
    const sortMethod = getters.getColumnSortMethod
    const sortedList = sortMethod(state.companies)

    if (state.searchTerm?.length) {
      const normalizedSearchTerm = state.searchTerm.toLowerCase()

      return fuzzysort
        .go(normalizedSearchTerm, sortedList, {
          keys: ['filter_values.name.formatted_value'],
        })
        .map((res) => res.obj)
    }
    return sortedList
  },
  getError(state) {
    return state.error
  },
  retriesExhausted(state) {
    return state.retries > MAX_RETRIES
  },
  pendingCompaniesRequest(state) {
    return state.pendingCompaniesRequest
  },
  errorCountsPerFilter(state, getters) {
    const allCompanies = getters.getAllCompanies

    return allCompanies.reduce((accumulator, company) => {
      for (const [key, filter] of Object.entries(company.filter_values)) {
        if (filter.filter_error) {
          accumulator[key] ||= 0
          accumulator[key] += 1
        }
      }
      return accumulator
    }, {})
  },
}

const mutations = {
  [mutationTypes.INIT_COMPANIES_REQUEST](state, requestRef) {
    // Reset companies and in-flight state
    state.companies = {}
    state.collectedCompanyIds = new Set()
    state.error = null
    state.pendingCompaniesRequest = {
      ref: requestRef,
      chunkCount: 0,
      receivedChunkIds: new Set(),
    }
  },

  [mutationTypes.INIT_COMPANIES_RETRY_REQUEST](state, requestRef) {
    state.pendingCompaniesRequest = {
      ref: requestRef,
      chunkCount: 0,
      receivedChunkIds: new Set(),
    }
  },

  [mutationTypes.SET_PENDING_COMPANIES_REQUEST](
    state,
    { requestRef, chunkCount, allCompanyIds }
  ) {
    // Ignore if this is the count update from another requestRef
    if (state.pendingCompaniesRequest.ref === requestRef) {
      state.pendingCompaniesRequest.chunkCount = chunkCount

      if (allCompanyIds) {
        state.allCompanyIds = new Set(allCompanyIds)
      }
    }
  },

  [mutationTypes.APPEND_TO_COMPANIES](state, data) {
    const matchedCompanyIds = new Set()
    data['matches'].forEach((match) => {
      // Using company.id as unique hash key
      const key = match['company']['id']
      const existingCompany = state.companies[key] || {}

      state.companies[key] = {
        ...existingCompany,
        ...match,
        id: key,
        filter_values: {
          ...existingCompany.filter_values,
          ...match.filter_values,
        },
      }
      //fix for Set.add not detecting changes properly in vuex
      matchedCompanyIds.add(key)

      if (!state.didAutoSortOnFilterError) {
        // when receiving results, we want to auto-sort only once on the first filter/column with error that we encounter
        const filterWithError = Object.keys(match.filter_values).find(
          (key) => !!match.filter_values[key].filter_error
        )

        if (filterWithError) {
          state.didAutoSortOnFilterError = true
          this.commit(mutationTypes.SET_SORT_COLUMN, {
            sortColumn: filterWithError,
          })
        }
      }
    })
    //fix for Set.add not detecting changes properly in vuex
    state.collectedCompanyIds = new Set([
      ...state.collectedCompanyIds,
      ...data['non_matches'],
      ...matchedCompanyIds,
    ])

    matchedCompanyIds.forEach((item) => state.selectedCompanies.add(item))
  },
  [mutationTypes.SET_CHUNK_DONE](state, chunkId) {
    state.pendingCompaniesRequest.receivedChunkIds.add(chunkId)
  },
  [mutationTypes.SET_ERROR](state, error) {
    state.error = error
  },
  [mutationTypes.INCREASE_RETRIES_VALUE](state) {
    state.retries = state.retries + 1
  },
  [mutationTypes.UPDATE_SEARCH_TERM](state, value) {
    state.searchTerm = value
  },
}

const actions = {
  async receivedQueryResults({ commit, dispatch, state }, data) {
    if (data['request_ref'] !== state.pendingCompaniesRequest.ref) {
      return
    }

    if (data['error']) {
      commit(mutationTypes.SET_ERROR, data['error'])
    } else if (data['matches']) {
      commit(mutationTypes.APPEND_TO_COMPANIES, data)
    } else if (data['chunk_done'] !== undefined) {
      commit(mutationTypes.SET_CHUNK_DONE, data['chunk_done'])

      dispatch('checkIfMissingAnyCompanies')
    } else if (data['segment_run_done']) {
      state.companiesFullyLoaded = true
      state.companiesFullyLoadedAt = data['finished_at']
      await dispatch('silentReloadCurrentSegment')
    }
  },

  checkIfMissingAnyCompanies({ state, dispatch, getters }) {
    const chunkIdsDifferentFromChunkCount =
      state.pendingCompaniesRequest.receivedChunkIds.size !==
      state.pendingCompaniesRequest.chunkCount

    if (chunkIdsDifferentFromChunkCount) {
      return
    }

    const diff = symmetricDifference(
      state.collectedCompanyIds,
      state.allCompanyIds
    )
    const idsAreIdentical = diff.size === 0

    if (idsAreIdentical) {
      return
    }

    dispatch('fetchMissingCompanies', Array.from(diff))

    if (getters.retriesExhausted) {
      SfLog.info(
        'Received all chunks done, but missing results for some companies',
        {
          extra: {
            missingIds: Array.from(diff),
            missingIdsCount: diff.size,
          },
        }
      )
    }
  },

  fetchFilteredCompanies(
    { commit, state, rootState, rootGetters, dispatch },
    { existingRequestRef = null, noCache = false } = {}
  ) {
    if (rootGetters.hasLedgerSpecificFiltersWithoutPeriodSelection) {
      dispatch('setDefaultPeriod')
    }

    // Generate unique request reference to have a way to filter out incoming results from other (previous) requests on the MessageBus channel
    // We decided not to create a new channel per request, but instead send all results to a single channel and filter based on a per-request reference
    const requestRef =
      existingRequestRef || rootGetters.lastSegmentRef || generateUuid()

    let pendingRequestRef
    if (state.pendingCompaniesRequest) {
      pendingRequestRef = state.pendingCompaniesRequest.ref
    }

    commit(mutationTypes.INIT_COMPANIES_REQUEST, requestRef)

    state.companiesFullyLoaded = false
    state.companiesFullyLoadedAt = null
    state.didAutoSortOnFilterError = false
    state.selectedCompanies = new Set()

    api
      .fetchFilteredCompanies({
        rootState,
        rootGetters,
        pendingRequestRef,
        requestRef,
        noCache,
      })
      .then((data) => {
        commit(mutationTypes.SET_PENDING_COMPANIES_REQUEST, {
          requestRef,
          chunkCount: parseInt(data.chunkCount),
          allCompanyIds: data.allCompanyIds,
        })
      })
  },

  fetchMissingCompanies(
    { commit, rootState, rootGetters, state, dispatch },
    missingCompanyIds
  ) {
    if (rootGetters.retriesExhausted) return

    if (rootGetters.hasLedgerSpecificFiltersWithoutPeriodSelection) {
      dispatch('setDefaultPeriod')
    }

    // Generate unique request reference to have a way to filter out incoming results from other (previous) requests on the MessageBus channel
    // We decided not to create a new channel per request, but instead send all results to a single channel and filter based on a per-request reference
    const requestRef = rootGetters.lastSegmentRef || generateUuid()

    let pendingRequestRef
    if (state.pendingCompaniesRequest) {
      pendingRequestRef = state.pendingCompaniesRequest.ref
    }

    commit(mutationTypes.INCREASE_RETRIES_VALUE)

    commit(mutationTypes.INIT_COMPANIES_RETRY_REQUEST, requestRef)

    api
      .fetchFilteredCompaniesRetry({
        rootState,
        pendingRequestRef,
        requestRef,
        missingCompanyIds,
      })
      .then((data) => {
        commit(mutationTypes.SET_PENDING_COMPANIES_REQUEST, {
          requestRef,
          chunkCount: parseInt(data.chunkCount),
        })
      })
  },
  selectCompany({ state }, companyId) {
    state.selectedCompanies.add(companyId)
  },

  deselectCompany({ state }, companyId) {
    state.selectedCompanies.delete(companyId)
  },

  updateSearchTerm({ commit, state, getters }, value) {
    commit(mutationTypes.UPDATE_SEARCH_TERM, value)
    // need to intersect the selectedCompanies with the list potentially filtered by a search term
    const companyIds = new Set(
      getters.sortedAndFilteredCompanies.map((c) => c.id)
    )

    state.selectedCompanies = new Set([
      ...state.selectedCompanies,
    ]).intersection(companyIds)
  },
}

export default {
  state() {
    return {
      companies: {},
      pendingCompaniesRequest: null,
      error: null,
      allCompanyIds: null,
      collectedCompanyIds: new Set(),
      retries: 0,
      selectedCompanies: new Set(),
      didAutoSortOnFilterError: false,
      searchTerm: null,
      companiesFullyLoaded: false,
    }
  },
  getters,
  mutations,
  actions,
}
